/**
 * LinkedIn Error Page Component
 *
 * Displays error information when LinkedIn OAuth flow fails
 */

import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';

const LinkedInErrorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const errorCode = searchParams.get('error') || 'unknown_error';
  const errorDescription = searchParams.get('description') || '';

  const getErrorMessage = (code: string): { title: string; message: string } => {
    switch (code) {
      case 'access_denied':
        return {
          title: 'Access Denied',
          message:
            'You denied the LinkedIn authorization request. Please try again if you wish to participate in this survey.',
        };
      case 'missing_params':
        return {
          title: 'Missing Parameters',
          message: 'The authorization request was missing required parameters. Please try again.',
        };
      case 'invalid_state':
        return {
          title: 'Session Expired',
          message:
            'Your authorization session has expired. Please start the verification process again.',
        };
      case 'survey_not_found':
        return {
          title: 'Survey Not Found',
          message: 'The survey you are trying to access could not be found.',
        };
      case 'token_exchange_failed':
        return {
          title: 'Authentication Failed',
          message: 'We could not complete the LinkedIn authentication. Please try again.',
        };
      case 'profile_fetch_failed':
        return {
          title: 'Profile Fetch Failed',
          message: 'We could not retrieve your LinkedIn profile information. Please try again.',
        };
      case 'apify_not_configured':
        return {
          title: 'Service Unavailable',
          message:
            'The profile verification service is not available. Please contact the survey administrator.',
        };
      case 'session_creation_failed':
        return {
          title: 'Session Creation Failed',
          message: 'We could not start your survey session. Please try again.',
        };
      default:
        return {
          title: 'Something Went Wrong',
          message: errorDescription || 'An unexpected error occurred. Please try again.',
        };
    }
  };

  const { title, message } = getErrorMessage(errorCode);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-8 h-8 text-amber-600" />
        </div>

        <h1 className="text-xl font-semibold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600 mb-6">{message}</p>

        {errorCode !== 'survey_not_found' && (
          <button
            onClick={() => window.history.back()}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium mb-3"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        )}

        <button
          onClick={() => navigate('/')}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Go Home
        </button>

        {errorDescription && errorCode !== 'unknown_error' && (
          <p className="mt-4 text-xs text-gray-400">Error code: {errorCode}</p>
        )}
      </div>
    </div>
  );
};

export default LinkedInErrorPage;
